import { useMemo, useState } from 'react';

import Input from '@/components/Inputs/Input';
import { CascadingTree } from '@/components/Select/CascadingTree';
import treeSelectUtils from '@/components/Select/treeSelectUtils';
import type { TreeSelectItemBase } from '@/components/Select/types';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import { SOURCE_SEPERATOR } from '@/types/commonConst';
import {
  getFeedbackSourceName,
  getSourceIcon,
  type SOURCE_TYPE,
} from '@/types/feedbackConst';
import { isEmptyString } from '@/utils/util';

enum SourceCategory {
  SOURCE = 'source',
  DOMAIN = 'domain',
  CHANNEL = 'channel',
}

type SourceMeta =
  | {
      category: SourceCategory.SOURCE;
      source: SOURCE_TYPE;
    }
  | {
      category: SourceCategory.DOMAIN;
      source: SOURCE_TYPE;
      domain: StringNumberValue;
    }
  | {
      category: SourceCategory.CHANNEL;
      source: SOURCE_TYPE;
      domain: StringNumberValue;
      channel: StringNumberValue;
    };

export const StructuredSourceSelector = (props: {
  values: FilterKeyValue[];
  onChange: (values: FilterKeyValue[]) => void;
}) => {
  const [searchValue, setSearchValue] = useState('');

  const sourceInfo = useFeedbackSourceStore((state) => state.sourceInfo);

  const treeData: TreeSelectItemBase<SourceMeta>[] = useMemo(() => {
    return (
      sourceInfo?.map((item) => {
        const sourceName = getFeedbackSourceName(item.source);
        const sourceIcon = getSourceIcon(item.source);

        return {
          alias: item.source.toString(),
          searchValue: sourceName,
          meta: {
            category: SourceCategory.SOURCE,
            source: item.source,
          },
          node: (
            <div className="flex items-center gap-2">
              {sourceIcon}
              {sourceName}
            </div>
          ),
          childNodes: item.domains?.map((domain) => ({
            alias: `${item.source}${SOURCE_SEPERATOR}${domain.id}`,
            searchValue: domain.name,
            node: domain.name,
            meta: {
              category: SourceCategory.DOMAIN,
              source: item.source,
              domain: domain.name,
            },
            childNodes: domain.channels?.map((channel) => ({
              alias: `${item.source}${SOURCE_SEPERATOR}${domain.id}${SOURCE_SEPERATOR}${channel.id}`,
              searchValue: channel.name,
              node: channel.name,
              meta: {
                category: SourceCategory.CHANNEL,
                source: item.source,
                domain: domain.name,
                channel: channel.name,
              },
            })),
          })),
        };
      }) || []
    );
  }, [sourceInfo]);

  const filteredTreeData = useMemo(() => {
    if (isEmptyString(searchValue)) return treeData;
    return treeSelectUtils.getFlattenChildNodes(treeData, searchValue);
  }, [treeData, searchValue]);

  return (
    <div className="flex flex-col">
      <div className="p-1">
        <Input
          style={{ width: '100%', height: 32 }}
          placeholder="Search..."
          name="searchValue"
          sizeType="m"
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
        />
      </div>
      <div className="w-100 h-120 overflow-y-auto">
        <CascadingTree
          tree={filteredTreeData}
          values={props.values}
          onChange={props.onChange}
          parser={(item) => {
            switch (item.meta?.category) {
              case SourceCategory.SOURCE:
                return getFeedbackSourceName(item.meta?.source);
              case SourceCategory.DOMAIN:
                return item.meta?.domain.toString();
              case SourceCategory.CHANNEL:
                return item.meta?.channel.toString();
            }
            return '';
          }}
        />
      </div>
    </div>
  );
};
